import { z } from 'zod';
import { dateSchema, idSchema, nameSchema, objectIdSchema } from '../validation';

/**
 * Who proved an {@link IdentityProof}.
 *
 * - `bayan` — ELM accepted a `createTrip` carrying exactly these values.
 *   The only source that makes a number "accepted by Bayan".
 * - `staff` — a Hero staff member checked the cards and confirmed the
 *   values by hand (admin takeover). Trusted for display and to stop the
 *   agent re-asking, but NOT treated as Bayan-accepted: issuance still
 *   sweeps the issue number and the carrier numbers.
 */
export const identityProofBySchema = z.enum(['bayan', 'staff']);
export type IdentityProofBy = z.infer<typeof identityProofBySchema>;

/**
 * One snapshot of a filing that was proven — the driver, the truck and the
 * carrier exactly as they were sent (and, for `by: 'bayan'`, as ELM
 * accepted them).
 *
 * ## Same snapshot on both rows
 *
 * The SAME object is written onto `Driver.legal.identityProof` and
 * `Vehicle.registration.identityProof` by one issuance (or one staff
 * verify). Hero drivers switch trucks, so each row keeps the whole
 * pairing it was last proven in: the truck can say "last accepted with
 * driver X under carrier 70…", and the driver can say "last accepted on
 * plate Y".
 *
 * ## How it is read
 *
 * Never trust a proof by its presence alone — compare it against the
 * row's CURRENT values with the `@hero/core` helpers:
 *
 * - `isDriverIdentityProven(driver)` — `identityNumber` + `issueNumber`
 *   equal the driver's `legal.id` + `legal.idIssueNumber`.
 * - `isVehicleIdentityProven(vehicle)` — `plate` (+ `plateType` when set)
 *   equal the registration's.
 * - `isPairProven(driver, vehicle)` — both rows carry the same `tripId`.
 *
 * Editing a compared field therefore un-proves the row on its own; no
 * writer has to clear the proof.
 *
 * ## Who writes it
 *
 * - Issuance on a successful `createTrip` (`recordIdentityProof` in
 *   `logisti-waybill.service.ts`), from the accepted payload.
 * - The background PDF parse after delivery fills the printed names
 *   (`driverName`, `carrierName`, `carrierLicense`, `waybillNumber`) on
 *   both copies, matched by `tripId`.
 * - Staff verify in the admin (`agent.thread-ops.service.ts`) with
 *   `by: 'staff'`.
 *
 * Never written by a form, the vision reader, or the agent.
 *
 * See `docs/adr/011-identity-fields.md`.
 */
export const identityProofSchema = z.object({
  /** Who proved it — see {@link identityProofBySchema}. */
  by: identityProofBySchema,
  /** When it was proven (issuance time / staff save time). */
  at: dateSchema,
  /** Bayan trip id of the accepted filing. Set when `by === 'bayan'`; the pairing key for `isPairProven`. */
  tripId: z.string().trim().min(1).optional(),
  /** رقم الوثيقة as printed on the issued PDF. Filled by the background PDF parse. */
  waybillNumber: z.string().trim().min(1).optional(),
  /** The staff member who verified. Set when `by === 'staff'`. */
  staffUserId: objectIdSchema.optional(),

  // ─── the driver as filed ──────────────────────────────────────────
  /** Hero `Driver._id` that was on the filing. Lets a truck name the driver it was proven with. */
  driverRef: objectIdSchema.optional(),
  /** رقم هوية السائق that was sent (and accepted). Compared with `Driver.legal.id`. */
  identityNumber: idSchema.optional(),
  /**
   * رقم النسخة that was sent (and accepted). NOT printed on the PDF — it
   * only exists in the payload. Compared with `Driver.legal.idIssueNumber`.
   */
  issueNumber: z.number().int().positive().optional(),
  /** اسم السائق as Bayan printed it on the PDF. Display only. */
  driverName: nameSchema.optional(),

  // ─── the truck as filed ───────────────────────────────────────────
  /** Hero `Vehicle._id` that was on the filing. Lets a driver name the truck it was proven on. */
  vehicleRef: objectIdSchema.optional(),
  /** Canonical plate that was sent. Compared with `Vehicle.registration.plate`. */
  plate: z.string().trim().min(1).optional(),
  /** `VehiclePlateType` key that was sent (`public_transport`, …). Compared when set. */
  plateType: z.string().trim().min(1).optional(),

  // ─── the carrier as filed ─────────────────────────────────────────
  /** رقم المنشأة — the 70… sent as Bayan `carrier.moi`. Mirrored onto `carrierIdentity` when `by === 'bayan'`. */
  carrierMoi: idSchema.optional(),
  /** الناقل name as printed on the PDF. Display only. */
  carrierName: nameSchema.optional(),
  /** رقم الترخيص of the carrier, when the PDF prints it. Display only. */
  carrierLicense: z.string().trim().min(1).optional(),
});
export type IdentityProof = z.infer<typeof identityProofSchema>;
